import { DatagridWithBlockDeleteProps } from "components/_helpers/props/DatagridProps";
import { Datagrid } from "components/CustomizableDataGrid";
import { CopyField } from "components/fields/CopyField";
import { DateTimeField } from "components/fields/DateFormatField";
import { SelectEnumField } from "components/fields/SelectEnumField";
import { ValueChipWithType } from "components/fields/ValueChipWithType";
import { StorageContextType } from "entities/StorageContextType";
import { StorageEntityType } from "entities/StorageEntityType";
import { StorageTransactionDirection } from "entities/StorageTransactionDirection";
import React from "react";
import { FunctionField, NumberField, TextField, useTranslate } from "react-admin";
import { StorageLinkChip } from "resources/orders/Show/PalletsAndBoxes/components/StorageLinkChip";
import { plainNumber } from "utils/constants";
import { getPalletOrderPath } from "utils/navigation";

const StoragePlaceField = ({ record }) => {
	const address = record?.storage_place?.address;

	const handleChipClick = (e) => {
		e.stopPropagation();
	};

	if (!record?.storage_place_id) return null;

	const content = (
		<span onClick={handleChipClick}>
			<StorageLinkChip
				pallet={{ storage_place_id: record.storage_place_id }}
				storagePlaces={{ [record.storage_place_id]: record.storage_place }}
				terminalId={record.storage_context.terminal_id}
			/>
		</span>
	);

	if (address) {
		return (
			<CopyField value={address} copyByIcon>
				{content}
			</CopyField>
		);
	}

	return <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>{content}</span>;
};

const StorageContextField = ({ record }) => {
	const translate = useTranslate();
	const contextTypeLabels = {
		[StorageContextType.order]: translate("osfc.enums.storage_context_type.order"),
		[StorageContextType.order_deviation]: translate(
			"osfc.enums.storage_context_type.order_deviation"
		),
		[StorageContextType.task]: translate("osfc.enums.storage_context_type.task"),
	};
	const contextConfigByType = {
		[StorageContextType.order]: {
			reference: "orders",
			idField: "order_id",
			chipSource: "reference",
			path: (id) => `#/orders/${id}/show`,
		},
		[StorageContextType.order_deviation]: {
			reference: "deviations",
			idField: "order_deviation_id",
			chipSource: "id",
			path: (id) => `#/deviations/${id}/show`,
		},
		[StorageContextType.task]: {
			reference: "tasks",
			idField: "task_id",
			chipSource: "title",
			path: (id) => `#/tasks/${id}/show`,
		},
	};

	if (!record?.storage_context) return null;
	const contextType = record.storage_context.type;
	const contextConfig = contextConfigByType[contextType];
	if (!contextConfig) return null;

	let value = null;
	let link = null;

	if (contextType === StorageContextType.order) {
		const order = record.storage_context.order;
		value = order?.reference || record.storage_context.order_id;
		const orderId = order?.id || record.storage_context.order_id;
		link = orderId ? contextConfig.path(orderId) : null;
	} else if (contextType === StorageContextType.order_deviation) {
		value = record.storage_context.order_deviation_id;
		link = value ? contextConfig.path(value) : null;
	} else if (contextType === StorageContextType.task) {
		const task = record.storage_context.task;
		value = task?.title || record.storage_context.task_id;
		const taskId = task?.id || record.storage_context.task_id;
		link = taskId ? contextConfig.path(taskId) : null;
	}

	if (!value) return null;

	return <ValueChipWithType value={value} link={link} typeLabel={contextTypeLabels[contextType]} />;
};

const StorageEntityField = ({ record }) => {
	const translate = useTranslate();
	const entityTypeLabels = {
		[StorageEntityType.pallet]: translate("osfc.enums.storage_entity_type.pallet"),
	};

	const entityConfigByType = {
		[StorageEntityType.pallet]: {
			reference: "pallets",
			idField: "pallet_id",
			chipSource: "sscc",
			path: () => getPalletOrderPath(record?.storage_entity?.pallet),
		},
	};

	if (!record?.storage_entity) return null;
	const entityConfig = entityConfigByType[record.storage_entity.type];
	if (!entityConfig) return null;
	const entityId = record.storage_entity[entityConfig.idField];
	if (!entityId) return null;

	const sscc = record.storage_entity?.pallet?.sscc || entityId;
	const palletId = record.storage_entity?.pallet_id;
	const link = entityConfig.path(entityId);
	if (!sscc) return null;

	const entityLabel = entityTypeLabels[record.storage_entity.type];
	const typeLabel = palletId ? `${entityLabel} #${palletId}` : entityLabel;

	return <ValueChipWithType value={sscc} link={link} typeLabel={typeLabel} />;
};

export const StorageTransactionsDatagrid = ({ terminals, t, columnsAsyncStorage }) => {
	return (
		<Datagrid
			{...DatagridWithBlockDeleteProps}
			rowClick="show"
			storage={columnsAsyncStorage}
			reorderingSupport={true}
		>
			<NumberField source="id" {...plainNumber} />
			<SelectEnumField
				source="direction"
				enumObj={StorageTransactionDirection}
				enumName="storage_transaction_direction"
			/>
			<FunctionField
				source="storage_place_id"
				label={t("storage_place")}
				render={(record) => <StoragePlaceField record={record} />}
				sortable={false}
			/>
			<FunctionField
				source="storage_entity_id"
				label={t("storage_entity")}
				render={(record) => <StorageEntityField record={record} />}
				sortable={false}
			/>
			<NumberField
				source="storage_entity.pallet.id"
				label={t("storage_entity_pallet_id")}
				sortable={false}
				{...plainNumber}
			/>
			<TextField
				source="storage_entity.pallet.sscc"
				label={t("storage_entity_pallet_sscc")}
				sortable={false}
			/>
			<NumberField
				source="storage_entity.pallet.ext_id"
				label={t("storage_entity_pallet_ext_id")}
				sortable={false}
				{...plainNumber}
			/>
			<TextField
				source="storage_entity.pallet.sscc_original"
				label={t("storage_entity_pallet_sscc_original")}
				sortable={false}
			/>
			<FunctionField
				source="storage_context_id"
				label={t("storage_context_entity")}
				render={(record) => <StorageContextField record={record} />}
				sortable={false}
			/>
			<FunctionField
				source="storage_context.terminal_id"
				label={t("storage_context_terminal")}
				sortable={false}
				render={(record) =>
					terminals?.[record?.storage_context?.terminal_id]?.name ??
					record?.storage_context?.terminal_id
				}
			/>
			<DateTimeField source="timestamp" />
			<DateTimeField source="planned_timestamp" />
			<DateTimeField source="modified_at" />
			<TextField
				source="user.full_name"
				emptyText="System"
				label={t("modified_by")}
				sortable={false}
			/>
		</Datagrid>
	);
};
